import { FormEvent, useEffect, useState } from "react";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import type { StockProduct } from "@/core/pharmacy/types";

interface QuickPharmacyMovementModalProps {
  open: boolean;
  mode: "receipt" | "issue";
  product: StockProduct;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

export function QuickPharmacyMovementModal({
  open,
  mode,
  product,
  onClose,
  onSuccess,
}: QuickPharmacyMovementModalProps) {
  const [qty, setQty] = useState("1");
  const [storeId, setStoreId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQty("1");
    setError(null);
    void pharmacyInventoryApi.listStores().then((stores) => {
      if (stores[0]) setStoreId(stores[0].id);
    });
  }, [open, product.id]);

  if (!open) return null;

  const title = mode === "receipt" ? "Entrée en stock" : "Sortie de stock";
  const verb = mode === "receipt" ? "Enregistrer l'entrée" : "Enregistrer la sortie";

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const qtyNum = Number(qty);
    if (!storeId || qtyNum <= 0) {
      setError("Quantité et magasin requis.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (mode === "receipt") {
        await pharmacyInventoryApi.postReceipt(product.id, storeId, qtyNum);
        onSuccess(`${qtyNum} unité(s) entrée(s) pour « ${product.label} ».`);
      } else {
        await pharmacyInventoryApi.postIssue(product.id, storeId, qtyNum);
        onSuccess(`${qtyNum} unité(s) sortie(s) pour « ${product.label} ».`);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Mouvement impossible");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ph-modal-backdrop" onClick={onClose}>
      <div className="ph-modal-card" onClick={(e) => e.stopPropagation()}>
        <h2 className="h5 mb-1">{title}</h2>
        <p className="text-muted small mb-3">{product.label}</p>
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label className="form-label">Quantité</label>
            <input
              type="number"
              min="1"
              className="form-control"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              required
              autoFocus
            />
          </div>
          {error && <div className="alert alert-danger py-2 small">{error}</div>}
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Enregistrement…" : verb}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
